import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { fetchImage } from "../../../services/annotation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { axios } from "../../../helpers/axios";
import { doesUserHasAccess, formatString } from "../../../helpers/utils";
import LivisButton from "../../../components/Form/Button/LivisButton";
import { livisAlert } from "../../../helpers/alert";
import LivisModal from "../../../components/Modals/LivisModal";
import LandingMain from "../../../components/Layouts/Landing/MainLayout";
import { setIsLoadingTranslucent } from "../../../store/actions";
import useDocumentTitle from "../../../helpers/useDocumentTitle";
import { findUser } from "../../../helpers/finduser";

// Main component for Annotation ROI
const AnnotationROI = () => {
  // Debug log
  console.log("Came here......................");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { part_id, image_id } = useParams();
  // Remove ':' from params for backend compatibility
  const formattedPartId = part_id.replace(":", "");
  const formattedImageUrl = image_id.replace(":", "");

  // State variables
  const [handleAction, setHandleAction] = useState("");
  const [annotationOuput, setAnnotationOuput] = useState({});
  const [partDetails, setPartDetails] = useState([]);
  const [imageCount, setImageCount] = useState({
    current_image_index: 0,
    total_images: 1, // Since we are working with a single image
  });
  const [imageUrl, setImageUrl] = useState("");
  const processName = useSelector((state) => state.processNameReducer);
  const [defectLibrary, setDefectLibrary] = useState([]);
  const [checkedState, setCheckedState] = useState([]);
  const [enableGenerate, setEnalbleGenerate] = useState(false);
  const [defectLibraryMapping, setDefectLibraryMapping] = useState([]);
  const [validation, setValidation] = useState(true);
  // const [domLoaded, setDomLoaded] = useState(false);
  const [syntheticDataProgress, setSyntheticDataProgress] = useState([]);
  const [progressbar, setProgressBar] = useState(false);
  const [syntheticImagesModal, setSyntheticImagesModal] = useState(false);
  const [syntheticImages, setSyntheticImages] = useState([]);
  const [annotatedImageData, setAnnotatedImageData] = useState([]);
  const [toggleUdt, setToggleUdt] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [annotationImageCount, setAnnotationImageCount] = useState(0);

  // const augmentation_icon = process.env.PUBLIC_URL + '/image/icons/augmentation.svg'

  // Fetch defect library and part defects for the current part/image
  const getDefectsList = async () => {
    const payload = {
      part_id: formattedPartId,
      process_name: processName,
      image_document_id: formattedImageUrl,
    };
    const response = await axios.post(
      "/synthetic_data/get_defect_library_and_part_defects/",
      payload
    );
    console.log(response);
    setDefectLibrary(response?.data?.data);
  };

  // Props for the landing page layout
  const landing_page_props = {
    livis_logo: process.env.PUBLIC_URL + "/image/icons/lincode_livis.svg",
    search_icon: process.env.PUBLIC_URL + "/image/icons/search.svg",
    livis_hamburger: process.env.PUBLIC_URL + "/image/icons/hamburger.svg",
    setting_icon: process.env.PUBLIC_URL + "/image/icons/settings.svg",
    setting_icon_bold:
      process.env.PUBLIC_URL + "/image/icons/settings_bold.svg",
    alert_icon: process.env.PUBLIC_URL + "/image/icons/alert.svg",
    alert_icon_bold: process.env.PUBLIC_URL + "/image/icons/alert_bold.svg",
    profile_icon: process.env.PUBLIC_URL + "/image/icons/profile.svg",
    intro_icon: process.env.PUBLIC_URL + "/image/misc/intro_icon.svg",
    page_title: "Inspection Station",
  };

  // Fetch the image URL and annotation details
  const getImage = async () => {
    try {
      const response = await fetchImage(formattedPartId, formattedImageUrl);
      const fileUrl = response?.data?.data?.file_url;

      // Set annotation count if available
      if (response?.data?.data?.annotation_detection) {
        setAnnotationImageCount(
          response?.data?.data?.annotation_detection.length
        );
      }

      // Prepare annotation data for UDT
      let temp_data = {};
      temp_data["annotation"] = response?.data?.data?.annotation_detection?.map(
        (item) => {
          const x = parseFloat(item.x);
          const y = parseFloat(item.y);
          const w = parseFloat(item.w);
          const h = parseFloat(item.h);

          const centerX = x + w / 2;
          const centerY = y + h / 2;
          return {
            classification: item.cls,
            color: item.color,
            centerX: centerX,
            centerY: centerY,
            width: w,
            height: h,
            regionType: "bounding-box",
          };
        }
      );
      setPartDetails([{ ...temp_data, imageUrl: fileUrl }]);
      console.log(temp_data, "temp_data");

      if (fileUrl) {
        setImageUrl(fileUrl);
      } else {
        console.error("Image URL not found in the response");
      }
    } catch (error) {
      console.error("Error fetching the image URL:", error);
    }
  };

  // Polling function to check synthetic data generation progress
  const startPolling = async () => {
    document.getElementById("udt").style.display = "none";
    // document.getElementById("#udt").style.opacity = 0

    let intervalId = null;
    const pollStatus = async () => {
      const payload = {
        part_id: formattedPartId,
        image_document_id: formattedImageUrl,
      };
      const response = await axios.post(
        "synthetic_data/get_synthetic_data_updates/",
        payload
      );
      if (response?.status != 200) {
        livisAlert(`${t("Error in generating synthetic data")}`, "error");
        clearInterval(intervalId); // Stop polling
        document.getElementById("udt").style.display = "visible";
        return;
      }
      setProgressBar(true);
      setSyntheticDataProgress(response?.data?.data);

      if (response?.data?.data?.is_completed) {
        clearInterval(intervalId); // Stop polling
        setProgressBar(false);
      }
    };

    // Start polling every 5 seconds
    intervalId = setInterval(pollStatus, 5000);

    // Call once immediately to avoid waiting for the first interval
    pollStatus();
  };

  // On mount: check access, fetch image and defects
  useEffect(() => {
    const hasAccess = doesUserHasAccess(52);
    if (!hasAccess) {
      livisAlert(
        t(
          "You do not have access to this page. Please contact your administrator."
        ),
        "warning"
      );
      return;
    }
    getImage();
    getDefectsList();

    // for (const button of buttons) {
    //   console.log(button, "button")
    //   if(buttons.innerText == "Prev"){
    //     button.disabled = true
    //     console.log(button, "prev")
    //   }
    // }
  }, []); // Fetch image URL on component mount

  // Hide navigation buttons (if needed)
  const hideButtons = () => {
    // setDomLoaded(true)
    const buttons = document.getElementsByClassName("jss8");
    console.log(buttons, "buttons");
    if (buttons[0].style) {
      buttons[0].style.display = "none";
      buttons[1].style.display = "none";
      console.log(buttons[0].style, "0th button");
    }

    window.removeEventListener("DOMContentLoaded", hideButtons);
  };

  // useEffect(() => {
  //   hideButtons()
  // }, [domLoaded, defectLibrary]);
  // window.addEventListener('DOMContentLoaded', hideButtons);

  // Fetch all synthetic images for the part/image
  const getAllSyntheticImages = async () => {
    const payload = {
      part_id: formattedPartId,
      image_document_id: formattedImageUrl,
    };
    const response = await axios.post(
      "synthetic_data/get_synthetic_data/",
      payload
    );
    if (response?.status != 200) {
      livisAlert(`${t("Error Fetching Images")}`, "Error");
      return;
    }

    setSyntheticImages(response?.data?.data?.images_list);
    setToggleUdt(true);
    localStorage.setItem(
      "syntheticImages",
      JSON.stringify(response?.data?.data?.images_list)
    );
    // window.location.reload();

    console.log("reponse synthetic data", response?.data?.images_list);
    // setSyntheticImagesModal(true)
  };

  // useEffect(() => {
  //     const syntheticItem = localStorage.getItem("syntheticImages")
  //     if(syntheticItem){
  //       setSyntheticImagesModal(true)
  //       setSyntheticImages(JSONsyntheticItem)
  //     }
  // }, [])

  // Navigate to marked page when synthetic data generation is complete
  useEffect(() => {
    if (syntheticDataProgress?.is_completed == true) {
      // setSyntheticImagesModal(true)
      // getAllSyntheticImages()
      navigate("/app/class-based-augmentation/marked");
    }
  }, [syntheticDataProgress]);

  // Update partDetails when imageUrl changes
  useEffect(() => {
    if (imageUrl) {
      const tempData = {
        imageUrl: decodeURIComponent(imageUrl), // Decode URL in case it’s encoded
        annotation: [], // Initialize annotations as empty
      };

      setPartDetails([tempData]);
    }
  }, [imageUrl]); // Update partDetails whenever imageUrl changes

  // Initialize Universal Data Tool (UDT) when partDetails changes
  useEffect(() => {
    if (partDetails && partDetails.length > 0) {
      const udtContainer = document.getElementById("udt");
      console.log(partDetails, "part Details");
      window.UniversalDataTool.open({
        container: udtContainer,
        udt: {
          interface: {
            type: "image_segmentation",
            labels: ["ROI"],
          },
          samples: partDetails,
        },
        onSaveSample: (output) => {
          setAnnotationOuput(output);
          setIsSaved(true);
          console.log("Output ------------>", output);
        },
        onExit: (action) => {
          if (action === "go-to-next") {
            setHandleAction("next");
          } else if (action === "go-to-previous") {
            setHandleAction("prev");
          } else {
            setHandleAction("save");
          }
        },
      });
    }
  }, [partDetails]); // Update UDT when partDetails changes

  // Handle responsive positioning for back button
  const handleResize = () => {
    const mediaQuery = window.matchMedia("(max-width: 786px)");
    const backBtnWrap = document.getElementById("back-btn-wrap");

    if (mediaQuery.matches) {
      backBtnWrap.style.top = "auto";
      backBtnWrap.style.bottom = "1rem";
    } else {
      backBtnWrap.style.top = "1rem";
      backBtnWrap.style.bottom = "auto";
    }
  };

  // Handle checkbox selection for defect classes
  const handleCheckbox = (index) => {
    const defectName = defectLibrary.defect_library_list[index];
    const updatedState = checkedState.map((state, idx) =>
      idx === index ? !state : state
    );
    setCheckedState(updatedState);

    if (updatedState[index]) {
      // If checkbox is checked, add the defect with default values
      if (
        !defectLibraryMapping.some(
          (item) => item.defect_class_name === defectName
        )
      ) {
        setDefectLibraryMapping((prevState) => [
          ...prevState,
          {
            defect_class_name: defectName,
            actual_part_class_name: "", // Default value
            no_of_imgs_to_generate: 0, // Default value
            max_defect_per_image: 0, // Default value
          },
        ]);
      }
    } else {
      // If checkbox is unchecked, remove the defect and reset input fields
      setDefectLibraryMapping((prevState) =>
        prevState.filter((item) => item.defect_class_name !== defectName)
      );

      // Clear input fields by forcing re-render with empty values
      document.querySelectorAll(`[name="${defectName}"]`).forEach((input) => {
        input.value = ""; // Reset input fields
      });
    }

    // Enable/Disable the generate button based on selected defects
    setEnalbleGenerate(updatedState.includes(true));
  };

  // Initialize checkedState array when defectLibrary loads
  useEffect(() => {
    if (defectLibrary?.defect_library_list?.length) {
      setCheckedState(
        new Array(defectLibrary.defect_library_list.length).fill(false)
      );
    }
  }, [defectLibrary]);

  // Enable/disable generate button based on annotation and mapping
  useEffect(() => {
    if (
      annotationOuput?.annotation?.length &&
      formattedPartId &&
      formattedImageUrl &&
      processName &&
      defectLibraryMapping?.length > 0
    ) {
      setEnalbleGenerate(true);
    } else {
      setEnalbleGenerate(false);
    }
  }, [annotationOuput, defectLibraryMapping]);

  // Handle "Generate Data" button click
  const handleGenerate = async () => {
    const annotationData = annotationOuput?.annotation;
    // Only use new ROI annotations (skip already existing ones)
    const annotation =
      annotationData?.slice(annotationImageCount, annotationData?.length) || [];

    console.log("annotation count", annotationImageCount);
    console.log("annotation output", annotationData);
    console.log("ROI data", annotation);

    // const xmin = annotation?.centerX - annotation?.width / 2;
    // const ymin = annotation?.centerY - annotation?.height / 2;

    // const payload = {
    //   part_id: formattedPartId,
    //   image_document_id: formattedImageUrl,
    //   manufacturing_process: processName,
    //   region_of_interest: [ {
    //     x: xmin,
    //     y: ymin,
    //     w: annotation?.width,
    //     h: annotation?.height
    //     // x: 0,
    //     // y: 0,
    //     // w: 400,
    //     // h: 400
    // }],
    //   defect_library_mapping: defectLibraryMapping

    // }

    const regionOfInterest = annotation.map((item) => ({
      x: item.centerX - item.width / 2,
      y: item.centerY - item.height / 2,
      w: item.width,
      h: item.height,
    }));

    const payload = {
      part_id: formattedPartId,
      image_document_id: formattedImageUrl,
      manufacturing_process: processName,
      region_of_interest: regionOfInterest,
      defect_library_mapping: defectLibraryMapping,
    };
    let validationError = false;
    // Validate defect mapping fields
    payload?.defect_library_mapping?.map((item) => {
      if (item?.max_defect_per_image < 3 || item.max_defect_per_image > 5) {
        livisAlert(
          `${t(
            "Max Defects per image needs to be greater than or equal to 3 and less than or equal to 5"
          )}`,
          "error"
        );
        validationError = true;
        return;
      } else if (item?.no_of_imgs_to_generate > 20) {
        livisAlert(
          `${t(
            "No of Images to generate needs to be lesser than or equal to 20"
          )}`,
          "error"
        );
        validationError = true;
        return;
      }
    });
    // if(payload?.defect_library_mapping?.max_defect_per_image < 3 || payload?.defect_library_mapping?.max_defect_per_image > 5 ){
    //   livisAlert("Max Defects per image needs to be less than 5 and greater than 3", "error")
    //   return
    // }
    console.log(validationError, "payload");

    if (!validationError) {
      dispatch(setIsLoadingTranslucent(true));
      const response = await axios.post(
        "synthetic_data/update_augmentation_per_image_configurations_and_roi/",
        payload
      );
      if (response?.status != 200) {
        livisAlert(`${t("Error in generating synthetic data")}`, "error");
        dispatch(setIsLoadingTranslucent(false));
      }

      if (response?.status == 200) {
        startPolling();
        dispatch(setIsLoadingTranslucent(false));
      }
    }
    // navigate(`/app/testing/resolve/${formattedPartId}/${formattedImageUrl}`)
  };

  // Handle change for actual part class name select
  const handleActualPartClassName = (e) => {
    const { name, value } = e.target;
    setDefectLibraryMapping((prev) => {
      return prev.map((defect) =>
        defect.defect_class_name === name
          ? { ...defect, actual_part_class_name: value }
          : defect
      );
    });
  };

  // Handle change for number of images to generate
  const handleAddNumberOfImages = (e) => {
    const { name, value } = e.target;

    // Check if the input is a valid number and within the allowed range
    if (!/^\d*$/.test(value) || parseInt(value) < 1 || parseInt(value) > 20) {
      setValidation(false); // Invalid input
      livisAlert(
        `${t(
          "No of Images to generate needs to be lesser than or equal to 20"
        )}`,
        "error"
      );
      return;
    }

    setValidation(true); // Valid input

    const index = defectLibraryMapping.findIndex(
      (defect) => defect.defect_class_name === name
    );

    if (index !== -1) {
      setDefectLibraryMapping((prev) => {
        const updatedMapping = [...prev];
        updatedMapping[index] = {
          ...updatedMapping[index],
          no_of_imgs_to_generate: parseInt(value),
        };
        return updatedMapping;
      });
    }
  };

  // Handle change for max defects per image
  const handleMaxDefectPerImage = (e) => {
    const { name, value } = e.target;

    // Check if the input is a valid number and greater than 0
    if (!/^\d*$/.test(value) || parseInt(value) < 1) {
      setValidation(false); // Invalid input
      return;
    }

    setValidation(true); // Valid input

    const index = defectLibraryMapping.findIndex(
      (defect) => defect.defect_class_name === name
    );

    if (index !== -1) {
      setDefectLibraryMapping((prev) => {
        const updatedMapping = [...prev];
        updatedMapping[index] = {
          ...updatedMapping[index],
          max_defect_per_image: parseInt(value),
        };
        return updatedMapping;
      });
    }
  };
  // Debug log for synthetic data progress
  console.log(syntheticDataProgress, "Synthetic data");

  // Handle closing of synthetic images modal
  const handleCloseModal = () => {
    setSyntheticImagesModal(false);
    setToggleUdt(false);
    localStorage.removeItem("syntheticImages");
  };

  // Enable/disable generate button based on defect mapping, checked state, and save status
  useEffect(() => {
    const isAnyFieldEmpty = defectLibraryMapping.some(
      (defect) =>
        defect.actual_part_class_name.trim() === "" ||
        defect.no_of_imgs_to_generate <= 0 ||
        defect.max_defect_per_image <= 0
    );

    // Enable "Generate" button only if:
    // - At least one checkbox is checked
    // - No fields are empty
    // - "Save" button has been clicked
    if (checkedState.includes(true) && !isAnyFieldEmpty && isSaved) {
      setEnalbleGenerate(true);
    } else {
      setEnalbleGenerate(false);
    }
  }, [defectLibraryMapping, checkedState, isSaved]);

  return (
    // Main layout wrapper
    <LandingMain landing_page_props={landing_page_props}>
      <Grid container>
        {/* Left side: UDT annotation tool */}
        <Grid item lg={6.1} md={8} sm={12} xs={12} height={"100vh"}>
          <div
            id="udt"
            className="livis-annoatate-new-udt"
            style={{ height: "100vh" }}
          ></div>
        </Grid>
        {/* Right side: Synthetic data parameters and controls */}
        <Grid
          container
          lg={5.9}
          md={4}
          sm={12}
          xs={12}
          alignContent="flex-start"
          paddingBlock="0.75rem"
          paddingInline="0.5rem"
          spacing={1}
        >
          {/* Annotation Details Section */}
          <Grid
            item
            lg={12}
            md={12}
            sm={6}
            xs={12}
            className="livis-synthetic-data-parameters-wrap"
          >
            <div className="livis-operator-panel-info-wrap livis-reports-panel-preivew-info-wrap livis-add-parameter-bl-accordian-wrap livis-annotation-info-wrap ">
              <div className="livis-operator-panel-accordian-wrap livis-reports-preivew-accordian-wrap livis-define-bl-accordian-wrap">
                {/* Generate Data Button */}
                <LivisButton
                  label={"Generate Data"}
                  livis_click_function={handleGenerate}
                  disabled={!enableGenerate || !validation}
                />
                <Accordion expanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    className="livis-annotation-inso-accoridan-summary"
                  >
                    <Box className="livis-add-part-parameter-select-defect-feature-heading livis-add-part-parameter-bl-heading">
                      {t("Synthetic Data Generation Parameters")}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {/* Render defect library parameter controls */}
                      {defectLibrary?.defect_library_list?.length > 0 ? (
                        defectLibrary?.defects_per_part_list?.length > 0 ? (
                          defectLibrary?.defect_library_list?.map(
                            (defect, index) => (
                              <Grid
                                key={index}
                                item
                                xs={12}
                                container
                                spacing={2}
                                className="livis-synthetic-data-parameters-input"
                              >
                                {/* Library Class Name & Part Class Name (Single Row) */}
                                <Grid item xs={12} container>
                                  {/* Checkbox */}
                                  <Grid item mr={2}>
                                    <Checkbox
                                      onChange={() => handleCheckbox(index)}
                                      checked={checkedState[index]}
                                    />
                                  </Grid>

                                  {/* Typography (60%) */}
                                  <Grid
                                    item
                                    xs={5}
                                    container
                                    alignItems="start"
                                    justifyContent={"start"}
                                    flexDirection={"column"}
                                  >
                                    <Typography variant="body2">
                                      Library Class Name
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: "nowrap",
                                        marginRight: 2,
                                      }}
                                      className="livis-dashboard-top-values-pie-text"
                                    >
                                      <strong>
                                        {" "}
                                        {formatString(
                                          defect.replace(/_/g, " ")
                                        )}
                                      </strong>
                                    </Typography>
                                  </Grid>

                                  {/* TextField (40%) */}
                                  <Grid
                                    item
                                    xs={6}
                                    alignItems="start"
                                    justifyContent={"start"}
                                    flexDirection={"column"}
                                    className="livis-synthetic-data-text-field-wrap"
                                  >
                                    <Typography variant="body2">
                                      Part Class Name
                                    </Typography>
                                    <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      disabled={!checkedState[index]}
                                      onChange={handleActualPartClassName}
                                      style={{ width: "8rem" }}
                                      value={
                                        defectLibraryMapping.find(
                                          (item) =>
                                            item.defect_class_name === defect
                                        )?.actual_part_class_name || ""
                                      }
                                      name={defect}
                                      sx={{
                                        borderRadius: 2, // Rounded Input
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2, // Consistent border radius for TextField
                                        },
                                      }}
                                    >
                                      {defectLibrary?.defects_per_part_list?.map(
                                        (option) => (
                                          <MenuItem
                                            key={option}
                                            value={option}
                                            style={{ padding: "4px" }}
                                          >
                                            {option}
                                          </MenuItem>
                                        )
                                      )}
                                    </TextField>
                                  </Grid>
                                </Grid>

                                {/* No. of Images to Generate (Full Row with Styled Input) */}
                                <Grid
                                  item
                                  xs={12}
                                  container
                                  alignItems="center"
                                  className="livis-synthetic-data-image-inputs"
                                >
                                  <Grid item xs={8} justifyContent={"center"}>
                                    <Typography variant="body2">
                                      No. of Images to generate
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      disabled={!checkedState[index]}
                                      onChange={handleAddNumberOfImages}
                                      name={defect}
                                      inputProps={{
                                        style: { textAlign: "center" },
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2,
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>

                                {/* Max Defects per Image (Full Row with Styled Input) */}
                                <Grid
                                  item
                                  xs={12}
                                  container
                                  alignItems="center"
                                  className="livis-synthetic-data-image-inputs"
                                >
                                  <Grid item xs={8}>
                                    <Typography
                                      variant="body2"
                                      className="livis-dashboard-top-values-pie-text"
                                    >
                                      Maximum defects per image
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={4}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      disabled={!checkedState[index]}
                                      name={defect}
                                      onChange={handleMaxDefectPerImage}
                                      inputProps={{
                                        style: { textAlign: "center" },
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2,
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            )
                          )
                        ) : (
                          <Grid item xs={12}>
                            <Typography variant="body1" color="error">
                              Given image does not have any defects
                            </Typography>
                          </Grid>
                        )
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body1" color="error">
                            No defect library available for this part
                          </Typography>
                        </Grid>
                      )}

                      {/* Validation Message */}
                      {!validation && (
                        <Grid item xs={12} textAlign="center">
                          <Typography color="error" variant="body2">
                            * Number of images should be between 1 and 20.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </div>
            </div>
          </Grid>
          {/* {
            syntheticImages?.length 
            ?
            <Grid item lg={12} md={12} sm={6} xs={12}>
            <div className="livis-operator-panel-info-wrap livis-reports-panel-preivew-info-wrap livis-add-parameter-bl-accordian-wrap livis-annotation-info-wrap">
              <div className="livis-operator-panel-accordian-wrap livis-reports-preivew-accordian-wrap livis-define-bl-accordian-wrap">
                <Accordion expanded > 
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    className="livis-annotation-inso-accoridan-summary"
                  >
                    <Box className="livis-add-part-parameter-select-defect-feature-heading livis-add-part-parameter-bl-heading">
                        {t("Generated Synthetic Images")}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails style={{height: "30rem"}}>
                    <Grid container className="livis-usecase-card-wrap" style={{height: "70% !important", overflowY: "auto"}} >
                  {syntheticImages?.map((item, index) => (
                    <Grid key={index} item lg={4} md={3} sm={4} xs={6} className={"livis-manage-data-card-wrap"}>
                      <img src={item?.file_url} className="livis-part-data-card-thumbnail"

                      />
                    </Grid>
                  ))}
                </Grid>
                <Grid>
                        <LivisButton label={"Export"} livis_click_function={() => {setSyntheticImages([])}} />
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
  
                </div>
                
              </div>
            </Grid>
            :
            null
          } */}

          {}
          {/* <Grid item lg={12} md={12} sm={6} xs={12}>
          <div className="livis-operator-panel-info-wrap livis-reports-panel-preivew-info-wrap livis-add-parameter-bl-accordian-wrap livis-annotation-info-wrap">
            <div className="livis-operator-panel-accordian-wrap livis-reports-preivew-accordian-wrap livis-define-bl-accordian-wrap">
              <Accordion expanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1b-content"
                  id="panel1b-header"
                  className="livis-annotation-inso-accoridan-summary"
                >
                  <Box className="livis-add-part-parameter-select-defect-feature-heading livis-add-part-parameter-bl-heading">
                    {t("Shortcut Details")}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid item lg={12} display="flex" justifyContent="space-between" paddingBlock="8px" className="livis-annotation-info-details-wrap">
                    <span>{t("Save")}</span>
                    <span>S</span>
                  </Grid>
                  <Grid item lg={12} display="flex" justifyContent="space-between" paddingBlock="8px" className="livis-annotation-info-details-wrap">
                    <span>{t("Save & Next")}</span>
                    <span>D</span>
                  </Grid>
                  <Grid item lg={12} display="flex" justifyContent="space-between" paddingBlock="8px" className="livis-annotation-info-details-wrap">
                    <span>{t("Save & Previous")}</span>
                    <span>A</span>
                  </Grid>
                  <Grid item lg={12} display="flex" justifyContent="space-between" paddingBlock="8px" className="livis-annotation-info-details-wrap">
                    <span>{t("Toggle Cursor")}</span>
                    <span>W</span>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </Grid> */}

          {/* Annotation Notes Section */}
          {/* <Grid item lg={12} md={12} sm={12} xs={12}>
          <div className="livis-operator-panel-info-wrap livis-reports-panel-preivew-info-wrap livis-add-parameter-bl-accordian-wrap livis-annotation-info-wrap">
            <div className="livis-operator-panel-accordian-wrap livis-reports-preivew-accordian-wrap livis-define-bl-accordian-wrap">
              <Accordion expanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1c-content"
                  id="panel1c-header"
                  className="livis-annotation-inso-accoridan-summary"
                >
                  <Box className="livis-add-part-parameter-select-defect-feature-heading livis-add-part-parameter-bl-heading">
                    {t("Annotation Notes")}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography className="livis-modal-header-text livis-annotation-typography">
                    <Grid item lg={12} display="flex">
                      <Box fontSize={12}>
                        <strong>Note:</strong> &nbsp;
                        {t(
                          `After saving the image, click "Yes" to duplicate the bounding boxes to the next or previous unlabeled image. To apply this to all upcoming unlabeled images, click "Yes to All." To turn off this feature, click "Disable Annotation".`
                        )}
                      </Box>
                    </Grid>
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </Grid> */}
          {syntheticDataProgress?.is_completed != true ? (
            // Modal for showing synthetic data generation progress
            <LivisModal
              size="small"
              animation_type="slideup"
              modal_open={progressbar}
              title={`${t("Generating Synthetic Images")}`}
              disable_outside_click={true}
            >
              <>
                <div
                  className="livis-augmentation-data-generating-progress-container"
                  style={{ zIndex: 1000000000000 }}
                >
                  <div className="livis-augmentation-progress-details">
                    <div className="livis-augmentation-progress-details-wrap">
                      {/* <img src={augmentation_icon} alt="" /> */}
                      <span>
                        {
                          // (augmentationProgressDetails?.status == "running" || augmentationProgressDetails?.status == "started") ?
                          //     t("Verifying Data") : augmentationProgressDetails?.status == "augmentation started" ?
                          //         t("Augmentation Started") : augmentationProgressDetails?.status == "updating db" ?
                          //             t("Saving Augmented Data") : augmentationProgressDetails?.status == "completed" ?
                          //                 t("Augmentation Completed") : formatString(removeUnderscores(augmentationProgressDetails?.status ?? "_"))
                        }
                        Generating Images
                      </span>
                    </div>
                    <div className="livis-augmentation-progress-percentage">
                      {syntheticDataProgress?.total_images &&
                      syntheticDataProgress?.images_generated
                        ? parseInt(
                            (syntheticDataProgress?.images_generated /
                              syntheticDataProgress?.total_images) *
                              100
                          )
                        : 0}
                      %
                    </div>
                  </div>
                  <div className="livis-augmentation-progress-bar">
                    <div className="livis-augmentation-progress-bar">
                      <LinearProgress
                        variant="determinate"
                        color="primary"
                        value={
                          syntheticDataProgress?.total_images &&
                          syntheticDataProgress?.images_generated
                            ? (syntheticDataProgress?.images_generated /
                                syntheticDataProgress?.total_images) *
                              100
                            : 0
                        }
                      />
                    </div>
                  </div>
                  <div className="livis-augmentation-progress-message">
                    {t(
                      "Please wait while LIVIS generates the synthetic data needed for your model"
                    )}
                    ...
                  </div>
                </div>

                {/* <Grid container className='livis-part-manage-data-augmentation-container'>
                  <Button variant="outlined" size="medium" className="livis-modal-btn-round livis-modal-cancel-close-btn" onClick={() => handleStopAugmentation()}>
                      {t("Cancel")}
                  </Button>
              </Grid> */}
              </>
            </LivisModal>
          ) : null}

          {/* <LivisModal
            modal_open={syntheticImagesModal}
            size="medium"
            title={t("Synthetic Images")}
            disable_outside_click={true}
            cancel_icon={true}
            animation_type={"slideup"}
            livis_dismiss_btn={() => { setSyntheticImagesModal(false) }}
            livis_modal_class={"livis-synthetic-augmentation-modal"}
          >
            <>
            <div className="livis-usecase-data" style={{ height: "90%", overflowY: "auto" }} >
              <div className="livis-usecase-cards-wrap" style={{
                height: "70vh",
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: "1rem"
              }
              }>

                <Grid container className="livis-usecase-card-wrap" >
                  {syntheticImages?.map((item, index) => (
                    <Grid key={index} item lg={4} md={3} sm={4} xs={6} className={"livis-manage-data-card-wrap"}>
                      <img src={item?.file_url} className="livis-part-data-card-thumbnail"

                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            </div>
            <Grid container
              justifyContent="flex-end" className='livis-part-manage-data-augmentation-container'>
              <Button variant="outlined"
                style={{
                  borderColor: "#1a26c1",
                  color: "#1a26c1"
                }}
                size="medium" className="livis-modal-btn-round livis-modal-cancel-close-btn"
                onClick={() => setSyntheticImagesModal(false)}
              >
                {t("Cancel")}
              </Button>
              <Button size="medium"
                // type='primary'
                style={{
                  backgroundColor: "#1a26c1"
                }}
                variant="contained"
                className="livis-modal-btn-round livis-modal-cancel-close-btn"
                onClick={() => setSyntheticImagesModal(false)}
              >
                {t("Export")}
              </Button>
            </Grid>
            </>

          </LivisModal> */}

          {/* <LivisModal
                modal_open={syntheticImagesModal}
                size="medium"
                title={t("Synthetic Images")}
                disable_outside_click={true}
                cancel_icon={true}
                animation_type={"slideup"}
                livis_dismiss_btn={handleCloseModal}
                livis_modal_class={"livis-synthetic-augmentation-modal"}
              >
                <>
                <div className="livis-usecase-data" style={{ height: "90%", overflowY: "auto" }} >
                  <div className="livis-usecase-cards-wrap" style={{
                    height: "70vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                    paddingRight: "1rem"
                  }
                  }>
    
                    <Grid container className="livis-usecase-card-wrap" >
                      {syntheticImages?.map((item, index) => (
                        <Grid key={index} item lg={4} md={3} sm={4} xs={6} className={"livis-manage-data-card-wrap"}>
                          <img src={item?.file_url} className="livis-part-data-card-thumbnail"
    
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                </div>
                <Grid container
                  justifyContent="flex-end" className='livis-part-manage-data-augmentation-container'>
                  <Button variant="outlined"
                    style={{
                      borderColor: "#1a26c1",
                      color: "#1a26c1"
                    }}
                    size="medium" className="livis-modal-btn-round livis-modal-cancel-close-btn"
                    onClick={handleCloseModal}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button size="medium"
                    // type='primary'
                    style={{
                      backgroundColor: "#1a26c1"
                    }}
                    variant="contained"
                    className="livis-modal-btn-round livis-modal-cancel-close-btn"
                    onClick={handleCloseModal}
                  >
                    {t("Export")}
                  </Button>
                </Grid>
                </>
    
              </LivisModal> */}
        </Grid>
      </Grid>
    </LandingMain>
  );
};

export default AnnotationROI;
